import * as React from 'react';
import classNamesFn from 'classnames';

import {
  customCssClasses,
  getDataAttributes,
  getTabIndexAttribute,
  isEmptyObject,
  replaceCompIdPlaceholder,
  replaceContentIds,
  useAnalyticsReportClicks,
} from '@wix/editor-elements-common-utils';
import { IVectorImageProps } from '../VectorImage.types';
import Link from '../../Link/viewer/Link';
import semanticClassNames from '../VectorImage.semanticClassNames';
import styles from './style/VectorImage.scss';

const VectorImage: React.FC<IVectorImageProps> = props => {
  const {
    id,
    svgContent,
    shouldScaleStroke,
    withShadow,
    withStroke,
    link,
    ariaLabel,
    className = '',
    customClassNames = [],
    containerClass = '',
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    hasPlatformClickHandler,
    onKeyDown,
    toggle,
    reportBiOnClick,
  } = props;
  const classes = classNamesFn(
    styles.svgRoot,
    {
      [styles.nonScalingStroke]: !shouldScaleStroke,
      [styles.hasShadow]: withShadow,
      [styles.hasStroke]: withStroke,
      [styles.clickable]: hasPlatformClickHandler || onClick,
    },
    className,
  );

  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let listener: Parameters<HTMLDivElement['addEventListener']>[1];
    const component = ref.current;

    if (toggle) {
      listener = () => toggle(false);
      component?.addEventListener('click', listener);
    }

    return () => {
      if (listener) {
        component?.removeEventListener('click', listener);
      }
    };
  }, [ref, toggle]);

  const processedSvgContent = React.useMemo(() => {
    if (!svgContent) {
      return svgContent;
    }

    // avoid duplicate IDs in same document
    // mostly happens during page transitions - causes visual bugs - mostly on Safari
    const content = replaceContentIds(svgContent);

    return replaceCompIdPlaceholder(content, id);
  }, [id, svgContent]);

  const svgContentElement = (
    <div
      data-testid={`svgRoot-${id}`}
      className={classes}
      dangerouslySetInnerHTML={{
        __html: processedSvgContent,
      }}
    />
  );

  const handleClick = useAnalyticsReportClicks({
    onClick,
    reportBiOnClick,
  });

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      {...getTabIndexAttribute(props.a11y)}
      className={classNamesFn(
        containerClass,
        className,
        customCssClasses(semanticClassNames.root, ...customClassNames),
      )}
      onClick={handleClick}
      onDoubleClick={onDblClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
      ref={ref}
    >
      {!isEmptyObject(link) ? (
        <Link className={styles.link} aria-label={ariaLabel} {...link}>
          {svgContentElement}
        </Link>
      ) : (
        svgContentElement
      )}
    </div>
  );
};

export default VectorImage;
